import { ComponentIds, } from "@inter-ikea-core/core";
import StabilityZonesComponent from "../components/StabilityZonesComponent";
import { COMPONENT_IDS } from "../storage2ComponentIDs";
import { INSERTS_CONNECTOR, TABLE_CONNECTOR } from "../constants";
const isValidInsertsConnector = (connection) => {
    var _a;
    const isPointSeriesSubConnector = "series" in connection.hostConnector;
    const pointSeriesConnector = ((_a = connection.hostConnector.series) === null || _a === void 0 ? void 0 : _a.key) ===
        INSERTS_CONNECTOR;
    const pointConnector = connection.hostConnector.key === TABLE_CONNECTOR;
    const isInsertsConnector = pointSeriesConnector !== null && pointSeriesConnector !== void 0 ? pointSeriesConnector : pointConnector;
    return isPointSeriesSubConnector && isInsertsConnector;
};
/**
 * @returns Unstable filtered zones
 */
export const getUnstableZones = (stabilityZones) => Object.values(stabilityZones).filter(({ stable }) => !stable);
const getEntityHostConnections = (entity) => {
    var _a;
    const hostConnections = ((_a = entity.getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.connections) || [];
    return hostConnections;
};
/**
 * @returns The start position for a zone and if placement should go upwards/downwards
 */
export const getUnstableSections = (entities) => {
    const unstableSections = entities
        .map((entity) => {
        const stabilityZonesComponent = entity.getComponent(COMPONENT_IDS.STABILITY_ZONES);
        if (!stabilityZonesComponent || stabilityZonesComponent.stable)
            return null;
        const sectionPointsRequired = stabilityZonesComponent.sectionPointsRequired;
        const unstableZones = getUnstableZones(getStabilityZones(entity));
        const additionalPointsRequired = getAdditionalPointsRequired(sectionPointsRequired, Object.values(getStabilityZones(entity)));
        return {
            entity,
            unstableZones,
            additionalPointsRequired,
        };
    })
        .filter((section) => section !== null);
    return unstableSections;
};
/**
 * @returns Points required for section other than
 */
export const getAdditionalPointsRequired = (sectionPointsRequired, zones) => {
    const additionalPointsRequired = zones.reduce((acc, curr) => {
        acc = curr.points >= 1 ? acc - curr.points : acc - (1 + curr.points);
        return acc;
    }, sectionPointsRequired);
    return additionalPointsRequired;
};
export const getSectionInserts = (entity) => {
    const hostConnections = getEntityHostConnections(entity);
    return hostConnections.reduce((acc, connection) => {
        if (!isValidInsertsConnector(connection))
            return acc;
        return [...acc, connection];
    }, []);
};
const getInsertHoleSpan = (connection) => {
    var _a;
    const hole = connection.hostConnector.hole;
    const holeSpanning = (_a = connection.guestConnectorEntity.getComponent(COMPONENT_IDS.INSERT)) === null || _a === void 0 ? void 0 : _a.holesOccupied;
    if (!holeSpanning)
        throw new Error("No hole span found");
    return holeSpanning > 0
        ? [hole, hole + (holeSpanning - 1)]
        : [holeSpanning + 1 + hole, hole];
};
const numberIsWithinSpan = (number, [start, end]) => start <= number && number <= end;
const insertIsInZone = (zone) => (insert) => {
    const [insertStart, insertEnd] = getInsertHoleSpan(insert);
    const { min, max } = zone;
    return (numberIsWithinSpan(insertStart, [min, max]) ||
        numberIsWithinSpan(insertEnd, [min, max]) ||
        (insertStart < min && max < insertEnd));
};
const getInsertStabilityPoints = (insert) => {
    const insertComponent = insert.getComponent(COMPONENT_IDS.INSERT);
    if (!insertComponent)
        throw new Error("No insert component found.");
    return insertComponent.stabilityPoints;
};
const findZonesWhereInsertOccurs = (zones, insert) => Object.keys(zones).reduce((zonesArray, zoneKey) => {
    if (!insertIsInZone(zones[zoneKey])(insert))
        return zonesArray;
    return [...zonesArray, zoneKey];
}, []);
const generateUpdatedZone = (zoneKey, zone, points) => ({
    [zoneKey]: Object.assign(Object.assign({}, zone), { points: zone.points + points, stable: zone.points + points >= zone.requiredPoints }),
});
const calculateNewStabilityPointsForSection = (entity) => {
    const sectionInserts = getSectionInserts(entity);
    const stabilityZones = getDefaultStabilityZones(entity);
    return sectionInserts.reduce((zones, insert) => {
        const occurrences = findZonesWhereInsertOccurs(zones, insert);
        const points = getInsertStabilityPoints(insert.guestConnectorEntity) /
            occurrences.length;
        return occurrences.reduce((zones, zoneKey) => (Object.assign(Object.assign({}, zones), generateUpdatedZone(zoneKey, zones[zoneKey], points))), zones);
    }, stabilityZones);
};
export const getSectionTotalPoints = (stabilityZones) => Object.values(stabilityZones).reduce((totalPoints, zone) => totalPoints + zone.points, 0);
export const sectionIsStable = (stabilityZones, sectionPointsRequired) => Object.values(stabilityZones).every(({ stable }) => stable) &&
    getSectionTotalPoints(stabilityZones) >= sectionPointsRequired;
export const purgePreviousPoints = (stabilityZones) => Object.keys(stabilityZones).reduce((zones, zoneKey) => (Object.assign(Object.assign({}, zones), { [zoneKey]: Object.assign(Object.assign({}, stabilityZones[zoneKey]), { stable: false, points: 0 }) })), {});
export const getStabilityZones = (entity) => {
    var _a;
    const stabilityZones = (_a = entity.getComponent(COMPONENT_IDS.STABILITY_ZONES)) === null || _a === void 0 ? void 0 : _a.stabilityZones;
    if (!stabilityZones)
        throw new Error("No Stability component. Very fatal.");
    return stabilityZones;
};
export const getDefaultStabilityZones = (entity, required = true) => {
    var _a;
    const stabilityZones = (_a = entity.getComponent(COMPONENT_IDS.STABILITY_ZONES)) === null || _a === void 0 ? void 0 : _a.stabilityZones;
    if (required && !stabilityZones)
        throw new Error("No Stability component. Very fatal.");
    return purgePreviousPoints(stabilityZones);
};
export const updateStabilityZoneComponentInSection = (_entity) => {
    const stabilityZonesComponent = _entity.getComponent(COMPONENT_IDS.STABILITY_ZONES);
    if (!_entity.hasComponent(COMPONENT_IDS.SECTION_COMPONENT) ||
        !stabilityZonesComponent)
        return;
    const sectionPointsRequired = stabilityZonesComponent.sectionPointsRequired;
    const newStabilityZones = calculateNewStabilityPointsForSection(_entity);
    _entity.addComponent(new StabilityZonesComponent({
        stabilityZones: newStabilityZones,
        stable: sectionIsStable(newStabilityZones, sectionPointsRequired),
        sectionPointsRequired,
    }));
};
